import type { EPCR } from '@/types';

/**
 * narrative-quality — addendum § 11 two-layer audit.
 *
 * Layer 1 (syntax): complete sentences only. Fragments, trailing
 * commas, double spaces, or ALL-CAPS lines get flagged.
 *
 * Layer 2 (coverage): the narrative must mention the chief complaint,
 * at least one vital set, at minimum one disposition statement, and
 * must not contain patient PII that should have been redacted.
 *
 * The panel is advisory — the crew can still approve a narrative with
 * warnings — but gaps are surfaced before finalization so the crew
 * gets a chance to fix them.
 */

export interface NarrativeFinding {
  level: 'warn' | 'info';
  /** Short, human-readable label. */
  label: string;
  /** Longer explanation of the issue / remedy. */
  detail: string;
}

const PII_LEAK_PATTERNS: Array<{ re: RegExp; label: string }> = [
  { re: /\b\d{3}-\d{2}-\d{4}\b/, label: 'SSN-like pattern' },
  { re: /\b\d{3}-?\d{3}-?\d{4}\b/, label: 'phone-like pattern' },
];

const FRAGMENT_MIN_WORDS = 3;

/** Run both audit layers over the narrative text. */
export function auditNarrative(
  text: string,
  epcr: EPCR,
): NarrativeFinding[] {
  const findings: NarrativeFinding[] = [];
  const clean = (text ?? '').trim();

  if (!clean) {
    findings.push({
      level: 'warn',
      label: 'Empty narrative',
      detail: 'Generate or write a narrative before finalizing the ePCR.',
    });
    return findings;
  }

  // Layer 1 — syntax / sentence quality.
  const sentences = clean
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const fragments: string[] = [];
  const allCaps: string[] = [];
  for (const s of sentences) {
    const words = s.split(/\s+/).filter(Boolean);
    const endsCorrectly = /[.!?]"?$/.test(s);
    if (words.length < FRAGMENT_MIN_WORDS || !endsCorrectly) {
      fragments.push(s.slice(0, 60));
    }
    // ALL CAPS detection — ignore short lines (< 3 words)
    if (words.length >= 3 && s === s.toUpperCase() && /[A-Z]/.test(s)) {
      allCaps.push(s.slice(0, 60));
    }
  }
  if (fragments.length > 0) {
    findings.push({
      level: 'warn',
      label: `${fragments.length} sentence fragment${fragments.length === 1 ? '' : 's'}`,
      detail: `Found: "${fragments[0]}${fragments[0].length === 60 ? '…' : ''}". Ensure every sentence ends with . ! or ? and has at least ${FRAGMENT_MIN_WORDS} words.`,
    });
  }
  if (allCaps.length > 0) {
    findings.push({
      level: 'info',
      label: `${allCaps.length} ALL-CAPS line${allCaps.length === 1 ? '' : 's'}`,
      detail: 'Use sentence case — uppercase lines read as shouting and are harder for QA to parse.',
    });
  }
  if (/\s{2,}/.test(clean)) {
    findings.push({
      level: 'info',
      label: 'Double spaces detected',
      detail: 'Clean up runs of multiple spaces so the narrative formats correctly when printed.',
    });
  }

  // Layer 2 — coverage.
  const lower = clean.toLowerCase();

  if (epcr.chief_complaint && !lower.includes(epcr.chief_complaint.toLowerCase())) {
    findings.push({
      level: 'warn',
      label: 'Chief complaint not mentioned',
      detail: `The documented chief complaint ("${epcr.chief_complaint}") does not appear in the narrative.`,
    });
  }

  if (!/vital|blood pressure|bp|heart rate|hr|spo2|respirations|rr/i.test(clean)) {
    findings.push({
      level: 'warn',
      label: 'No vitals referenced',
      detail: 'Narratives should reference initial vital signs or the trend across the call.',
    });
  }

  const hasTransport = epcr.transport_decision === 'transport_this_unit'
    || epcr.transport_decision === 'transfer_other_unit';
  const hasRefusal = epcr.transport_decision === 'refusal_care'
    || epcr.transport_decision === 'refusal_transport';
  if (hasTransport && !/transport|delivered|arriv/i.test(clean)) {
    findings.push({
      level: 'warn',
      label: 'Transport not described',
      detail: 'Document transport destination and that care was transferred to receiving staff.',
    });
  }
  if (hasRefusal && !/refus|decline|against medical advice|ama/i.test(clean)) {
    findings.push({
      level: 'warn',
      label: 'Refusal not described',
      detail: 'Document informed refusal, capacity assessment, and risks explained.',
    });
  }

  // PII leak check — first/last names and DOB that should have been redacted.
  const first = (epcr.patient_first_name ?? '').trim();
  const last = (epcr.patient_last_name ?? '').trim();
  if (first.length >= 2 && new RegExp(`\\b${escapeRegExp(first)}\\b`, 'i').test(clean)) {
    findings.push({
      level: 'warn',
      label: 'Patient first name in narrative',
      detail: `Narratives should reference "the patient" — replace "${first}" throughout.`,
    });
  }
  if (last.length >= 2 && new RegExp(`\\b${escapeRegExp(last)}\\b`, 'i').test(clean)) {
    findings.push({
      level: 'warn',
      label: 'Patient last name in narrative',
      detail: `Narratives should reference "the patient" — replace "${last}" throughout.`,
    });
  }
  for (const { re, label } of PII_LEAK_PATTERNS) {
    if (re.test(clean)) {
      findings.push({
        level: 'warn',
        label: `Possible ${label}`,
        detail: 'Remove identifiers that do not belong in the clinical narrative.',
      });
    }
  }

  return findings;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
